import {alpha, SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {SystemStyleObject} from '@mui/system/styleFunctionSx/styleFunctionSx';

/**
 * The shared detail-page card.
 *
 * The panel had its own 8px radius, its own padding and `text.secondary` for a background - which is
 * white, but by way of a token that means "text on a dark ground". Its padding moves onto the chart,
 * so the heading and the filters can sit flush like every other section's.
 */
export const rootSx: SxProps<Theme> = {
    width: '100%',
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
    '.recharts-tooltip-wrapper': {
        outline: 'none',
    },
};

/**
 * The chart's own room, now that the card no longer pads everything at once.
 *
 * 12px above and below rather than 20: the chart already carries its own margin inside the SVG, so
 * the card's padding was being added to space the drawing had left itself, and the block read as
 * mostly empty between its heading and its bars.
 */
export const chartWrapperSx: SxProps<Theme> = {
    px: 2,
    py: 1.5,
    [theme.breakpoints.down('md')]: {
        px: 1,
    },
};

/**
 * The two controls, now that they sit on the heading's own line.
 *
 * `flexShrink: 0` is what keeps them on screen: the row they live in sets `min-width: 0` on its
 * children so a search box can shrink, and a 150px box with no floor shrinks all the way to nothing -
 * which is how these two managed to vanish rather than wrap. Below `md` they take the line instead.
 */
export const modeFieldSx: SxProps<Theme> = {
    width: theme.spacing(150 / 8),
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
    '& .MuiOutlinedInput-root': {
        height: '2rem',
        minHeight: '2rem',
        backgroundColor: theme.palette.common.white,
        [theme.breakpoints.down('md')]: {
            height: '44px',
            minHeight: '44px',
        },
    },
};

export const periodFieldSx: SxProps<Theme> = {
    width: theme.spacing(150 / 8),
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
    '& .MuiOutlinedInput-root': {
        height: '2rem',
        minHeight: '2rem',
        backgroundColor: theme.palette.common.white,
        [theme.breakpoints.down('md')]: {
            height: '44px',
            minHeight: '44px',
        },
    },
};
export const errorButtonSx: SystemStyleObject<Theme> = {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.2),
    },
};
export const errorButtonMenuSx: SystemStyleObject<Theme> = {
    maxWidth: theme.spacing(300 / 8),
    height: theme.spacing(32 / 8),
    width: '100%',
    mt: 2,
    mb: 1,
    [theme.breakpoints.down('lg')]: {
        m: 0,
    },
};

export const emptyStackSx: SxProps<Theme> = {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    py: 3,
    minHeight: '400px',
    [theme.breakpoints.down('md')]: {
        minHeight: '500px',
    },
};
export const titleSx: SxProps<Theme> = {
    marginBottom: theme.spacing(10 / 8),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('lg')]: {
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
    },
};

export const selectItemSx: SxProps<Theme> = {
    maxWidth: theme.spacing(150 / 8),
    width: '100%',
};
export const selectWrapperSx: SxProps<Theme> = {
    maxWidth: theme.spacing(450 / 8),
    width: '100%',
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('lg')]: {
        maxWidth: theme.spacing(150 / 8),
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        mb: 2,
    },
};

export const tooltipWrapperSx: SxProps<Theme> = {
    padding: theme.spacing(8 / 8),
    background: theme.palette.background.default,
    borderRadius: theme.spacing(8 / 8),
    position: 'relative',
    left: '-25px',
    boxShadow: '0 0 5px rgb(0, 0, 0, 0.25)',
    outline: 'none',
    border: 'none',
    '&:after': {
        position: 'absolute',
        content: '""',
        borderWidth: theme.spacing(8 / 8),
        borderColor: 'transparent',
        borderStyle: 'solid',
        borderTopColor: theme.palette.background.default,
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
    },
};
